import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { Blog, Comment, Reply, BlogType } from '@/models/blog';
import { isUndef } from '@/lib/utils';

export interface CommunityState {
  placard: string[];
  // 保存动态
  blogsMap: {
    [id: number]: Blog;
  };

  blogIds: {
    [hot_all_or_userId: string]: {
      list: number[];
      requesting: boolean;
      loaded: boolean;
    };
  };

  blogDetail: {
    [id: number]: Blog;
  };

  commentMap: {
    [id: number]: Comment;
  };

  commentIds: {
    [id: number]: {
      list: number[]; // id
      loaded: boolean;
    };
    requesting: boolean;
  };

  replyMap: {
    [id: number]: Reply;
  };

  replyIds: {
    [id: number]: {
      list: number[]; // id
      loaded: boolean;
    };
    requesting: boolean;
  };

  isShowShare: boolean;

  nav: BlogType;

  offlineList: Community.OfflineItem[];
  bannerUrl: Community.BannerUrl[];

  /** 战队动态 */
  moments: {
    [teamId: number]: ({ req: boolean } & ListWrap<number>) | undefined;
  };
  /** 战队动态详情 */
  momentMap: {
    [momentId: number]: Team.Moments;
  };

  /** 关注者动态 */
  followMoment: { req: boolean } & ListWrap<number>;
  /** 关注者动态详情 */
  followMomentMap: {
    [momentId: number]: Community.FollowMoment;
  };
}

const m: Module<CommunityState, RootState> = {
  namespaced: true,

  state: () => ({
    placard: [],

    /** 动态map */
    blogsMap: {},

    /** 动态列表 */
    blogIds: {},

    /**  动态详情 */
    blogDetail: {},

    /** 评论map */
    commentMap: {},

    /** 评论列表 */
    commentIds: {
      requesting: false,
    },

    /** 评论map */
    replyMap: {},

    /** 回复列表 */
    replyIds: {
      requesting: false,
    },

    /** 显示分享弹窗 */
    isShowShare: false,

    /** 导航栏 */
    nav: 'all',

    /** 线下活动列表 */
    offlineList: [],
    bannerUrl: [],

    /** 队伍动态 */
    moments: {},
    momentMap: {},

    followMoment: {
      list: [],
      loaded: false,
      req: false,
    },
    followMomentMap: {},
  }),

  mutations: {
   async loversRunCommit(state, data) {
      state.placard = await data;
      sessionStorage.setItem('loversImg' , JSON.stringify(data));
    },
    //       loversRun(state,data:string) {
    // Vue.axios.get('/api/v1/activity/special_list', {
    //           params:{
    //             activity_id:data,
    //           },
    //       }).then(response=> {
    //         state.placard = response.data;
    //       });
    //       },
    initBlogs(state, { key }) {
      Vue.set(state.blogIds, key, {
        list: [],
        loaded: false,
        requesting: false,
      });
    },

    /** 设置动态列表 */
    concatBlogs(state, { key, data }: { key: string | number; data: Blog[] }) {
      const blogIds: number[] = [];
      data.forEach((blog) => {
        blogIds.push(blog.id);
        Vue.set(state.blogsMap, blog.id, blog);
      });
      state.blogIds[key].list.push(...blogIds);
    },

    /**  设置请求 */
    setBlogsRequest(
      state,
      { key, data }: { key: string | number; data: boolean },
    ) {
      state.blogIds[key].requesting = data;
    },

    /** 设置加载 */
    setBlogsLoad(
      state,
      { key, data }: { key: string | number; data: boolean },
    ) {
      state.blogIds[key].loaded = data;
    },

    /** 删除一条动态 */
    removeBlog(state, data: Blog) {
      const blogTypes: BlogType[] = ['all', 'person', 'hot'];
      blogTypes.forEach((blogType) => {
        const wrap = state.blogIds[blogType];
        if (wrap && wrap.list) {
          const list = wrap.list;
          if (list.includes(data.id)) {
            list.splice(list.indexOf(data.id), 1);
          }
        }
      });
    },

    /** 设置动态详情 */
    setBlogDetail(state, { id, data }) {
      Vue.set(state.blogDetail, id, data);
    },

    /** 设置评论列表 */
    setComments(state, { blogId, list }) {
      if (!state.commentIds[blogId]) {
        Vue.set(state.commentIds, blogId, {
          list: [],
          loaded: false,
        });
      }
      const commentIds: number[] = [];
      list.forEach((comment: Comment) => {
        commentIds.push(comment.id);
        Vue.set(state.commentMap, comment.id, comment);
        if (comment.replies.length > 0) {
          // 处理评论携带的最新两条回复
          const replyIds: number[] = [];
          comment.replies.forEach((reply) => {
            replyIds.push(reply.id);
            Vue.set(state.replyMap, reply.id, reply);
          });
          Vue.set(state.replyIds, comment.id, {
            list: [],
            loaded: false,
          });
          // 因为重新请求了数据，清空一下回复列表
          state.replyIds[comment.id].list = [];
          state.replyIds[comment.id].list = state.replyIds[
            comment.id
          ].list.concat(replyIds);
        }
      });
      state.commentIds[blogId].list = state.commentIds[blogId].list.concat(
        commentIds,
      );
    },

    /**  设置评论请求 */
    setCommentsRequest(state, data) {
      state.commentIds.requesting = data;
    },

    /** 设置评论加载 */
    setCommentsLoad(state, { blogId, loaded }) {
      state.commentIds[blogId].loaded = loaded;
    },

    /** 设置回复列表 */
    setReplys(state, { commentId, list }) {
      const replyIds: number[] = [];
      if (!state.replyIds[commentId]) {
        Vue.set(state.replyIds, commentId, {
          list: [],
          loaded: false,
        });
      }
      list.forEach((reply: Reply) => {
        replyIds.push(reply.id);
        Vue.set(state.replyMap, reply.id, reply);
      });
      state.replyIds[commentId].list = state.replyIds[commentId].list.concat(
        replyIds,
      );
    },

    /**  设置回复请求 */
    setReplysRequest(state, data) {
      state.replyIds.requesting = data;
    },

    /** 设置回复加载 */
    setReplysLoad(state, { commentId, loaded }) {
      state.replyIds[commentId].loaded = loaded;
    },

    /** 是否显示分享弹窗 */
    setShowShare(state, data) {
      state.isShowShare = data;
    },

    /** 标记动态赞 */
    setBlogLike(state, data: Blog) {
      const count = data.like_count + 1;
      if (state.blogsMap[data.id]) {
        Vue.set(state.blogsMap[data.id], 'like_count', count);
        Vue.set(state.blogsMap[data.id], 'already_like', true);
      }
      if (state.blogDetail[data.id]) {
        Vue.set(state.blogDetail[data.id], 'like_count', count);
        Vue.set(state.blogDetail[data.id], 'already_like', true);
      }
      const moment = state.momentMap[data.id];
      if (moment) {
        moment.already_like = !moment.already_like;
        if (moment.already_like) {
          moment.like_count++;
        } else {
          moment.like_count--;
        }
      }
    },

    /** 取消动态赞 */
    setBlogDislike(state, data: Blog) {
      const count = data.like_count - 1;
      if (state.blogsMap[data.id]) {
        Vue.set(state.blogsMap[data.id], 'like_count', count);
        Vue.set(state.blogsMap[data.id], 'already_like', false);
      }
      if (state.blogDetail[data.id]) {
        Vue.set(state.blogDetail[data.id], 'like_count', count);
        Vue.set(state.blogDetail[data.id], 'already_like', false);
      }
      const moment = state.momentMap[data.id];
      if (moment) {
        moment.already_like = !moment.already_like;
        if (moment.already_like) {
          moment.like_count++;
        } else {
          moment.like_count--;
        }
      }
    },

    /** 添加评论 */
    addBlogComment(state, { id, content }: { id: number; content: string }) {
      if (state.blogDetail[id]) {
        const count = state.blogDetail[id].comment_count + 1;
        Vue.set(state.blogDetail[id], 'comment_count', count);
      }
      if (state.blogsMap[id]) {
        const count = state.blogsMap[id].comment_count + 1;
        Vue.set(state.blogsMap[id], 'comment_count', count);
      }
    },

    /** 删除评论 */
    removeBlogComment(
      state,
      { blogId, comment }: { blogId: number; comment: Comment },
    ) {
      const count = state.blogDetail[blogId].comment_count - 1;
      Vue.set(state.blogDetail[blogId], 'comment_count', count);
      // 动态列表有这条数据
      if (state.blogsMap[blogId]) {
        Vue.set(state.blogsMap[blogId], 'comment_count', count);
      }
      state.commentIds[blogId].list.splice(
        state.commentIds[blogId].list.indexOf(comment.id),
        1,
      );
    },

    /** 删除回复 */
    removeReply(state, { commentId, replyId }) {
      state.replyIds[commentId].list.splice(
        state.replyIds[commentId].list.indexOf(replyId),
        1,
      );
      state.commentMap[commentId]!.reply_count!--;
    },

    /** 设置导航栏 */
    setNav(state, nav: BlogType) {
      state.nav = nav;
    },

    /** 设置线下活动列表 */
    setOfflineList(state, list: Community.OfflineItem[]) {
      state.offlineList = list;
    },

    /** 设置顶部banner链接 */
    setBannerUrl(state, url) {
      state.bannerUrl = url;
    },

    /**
     * 队伍动态信息
     */
    setMoments(
      state,
      { teamId, moments }: { teamId: number; moments: Team.Moments[] },
    ) {
      const ids: number[] = [];
      for (const moment of moments) {
        ids.push(moment.id);
        Vue.set(state.momentMap, moment.id, moment);
      }
      state.moments[teamId]!.list.push(...ids);
    },
    setMomentLoaded(
      state,
      { teamId, loaded }: { teamId: number; loaded: boolean },
    ) {
      state.moments[teamId]!.loaded = loaded;
    },
    setMomentReq(state, { teamId, req }: { teamId: number; req: boolean }) {
      state.moments[teamId]!.req = req;
    },

    /**
     * 关注动态
     */
    concatFollowMoments(state, { moments }: { moments: Team.Moments[] }) {
      const ids: number[] = [];
      for (const moment of moments) {
        ids.push(moment.id);
        Vue.set(state.followMomentMap, moment.id, moment);
      }
      state.followMoment.list.push(...ids);
    },
    setFollowMomentLoaded(state, { loaded }: { loaded: boolean }) {
      state.followMoment.loaded = loaded;
    },
    setFollowMomentReq(state, { req }: { req: boolean }) {
      state.followMoment.req = req;
    },
  },

  actions: {
    /**
     * 获取动态列表
     * @param count 数量
     * @param last_id 最后一条id
     * @param mine 类型
     */
    async getBlogs(
      { state, commit, rootState },
      {
        count,
        lastId,
        type,
        userId,
        forceUpdate = false,
      }: {
        count: number;
        lastId: number | null;
        type: BlogType;
        userId: number;
        forceUpdate: boolean;
      },
    ) {
      if (!userId) {
        userId = rootState.user.basicInfo!.id;
      }
      const key = type === 'person' ? userId : type;

      let wrap = state.blogIds[key];
      if (!wrap) {
        commit('initBlogs', { key });
        wrap = state.blogIds[key];
      }

      if (forceUpdate) {
        wrap.requesting = false;
        wrap.loaded = false;
      }

      if (!lastId && !forceUpdate && wrap.list.length > 0) {
        return;
      }

      if (wrap.requesting || wrap.loaded) {
        return;
      }
      commit('setBlogsRequest', { key, data: true });
      const response = await Vue.axios.get('/api/v1/community/moment_list', {
        params: {
          count,
          last_id: lastId,
          mine: type,
          user_id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        if (forceUpdate) {
          wrap.list = [];
        }
        const list = rdata.data;
        commit('concatBlogs', { key, data: list });
        for (const blog of list) {
          commit(
            'user/setFollowInfo',
            {
              userId: blog.user_id,
              hasFollow: blog.has_follow,
            },
            { root: true },
          );
        }
        if (list.length < count) {
          commit('setBlogsLoad', { key, data: true });
        }
      }
      commit('setBlogsRequest', { key, data: false });
      return rdata;
    },

    /**
     * 发表动态
     * @param content 文字
     * @param images 图片key数组
     * @param imageUrls 图片url数组
     * @param position 定位
     */
    async setBlog(
      context,
      { content, images, imageUrls, position, sign_in_id },
    ) {
      const response = await Vue.axios.post('/api/v1/community/create_moment', {
        content,
        images,
        position,
        sign_in_id,
      });
      const rdata = response.data;
      return rdata;
    },

    /**
     * 获取动态详情
     * @param id 动态id
     */
    async getBlogDetail({ state, commit }, id: number) {
      if (state.blogDetail[id]) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/community/moment_detail', {
        params: {
          id,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setBlogDetail', { id, data: rdata.data });
      }
      return rdata;
    },
    async loversRun({ state, commit }, id: number) {
      const response = await Vue.axios.get('/api/v1/activity/special_list', {
        params: {
          activity_id: id,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('loversRunCommit', rdata.data);
      }
    },

    /**
     * 获取评论列表
     * @param id 动态id
     */
    async getBlogComments(
      { state, commit },
      { blogId, lastId, count, forceUpdate = false },
    ) {
      const data = state.commentIds[blogId]
        ? state.commentIds[blogId]
        : { list: [], loaded: false };
      if (state.commentIds.requesting || (data.loaded && !forceUpdate)) {
        return;
      }
      if (forceUpdate && !lastId) {
        data.list = [];
        data.loaded = false;
      }
      commit('setCommentsRequest', true);
      const response = await Vue.axios.get('/api/v1/community/comment_list', {
        params: {
          id: blogId,
          count,
          last_id: lastId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setComments', {
          blogId,
          list: rdata.data,
        });
        if (rdata.data.length < count) {
          commit('setCommentsLoad', {
            blogId,
            loaded: true,
          });
        }
      }
      commit('setCommentsRequest', false);
      return rdata;
    },

    /**
     * 获取回复列表
     * @param id 评论id
     */
    async getBlogReply(
      { state, commit },
      { commentId, lastId, count, forceUpdate = false },
    ) {
      const data = state.replyIds[commentId]
        ? state.replyIds[commentId]
        : { list: [], loaded: false };
      if (state.replyIds.requesting || (data.loaded && !forceUpdate)) {
        return;
      }
      if (forceUpdate && !lastId) {
        data.list = [];
        data.loaded = false;
      }
      commit('setReplysRequest', true);
      const response = await Vue.axios.get('/api/v1/community/reply_list', {
        params: {
          id: commentId,
          count,
          last_id: lastId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setReplys', {
          commentId,
          list: rdata.data,
        });
        if (rdata.data.length < count) {
          commit('setReplysLoad', {
            commentId,
            loaded: true,
          });
        }
      }
      commit('setReplysRequest', false);
      return rdata;
    },

    /**
     * 发表评论
     * @param content 文字儿
     * @param id 动态id
     */
    async setBlogComment(context, { id, content }) {
      const response = await Vue.axios.post(
        '/api/v1/community/create_comment',
        {
          content,
          id,
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('addBlogComment', { id, content });
      }
      return rdata;
    },

    /**
     * 发表回复
     * @param content 文字儿
     * @param id (评论的id)
     * @param reply_to 回复某个用户的用户id
     * @param reply_id 回复的id
     */
    async setBlogReply(context, { content, id, reply_to, reply_id }) {
      const response = await Vue.axios.post('/api/v1/community/create_reply', {
        content,
        id,
        reply_to,
        reply_id,
      });
      const rdata = response.data;
      return rdata;
    },

    /**
     * 删除动态
     * @param id
     */
    async deleteBlog(context, blog: Blog) {
      const response = await Vue.axios.post('api/v1/community/delete_moment', {
        id: blog.id,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('removeBlog', blog);
      }
      return rdata;
    },

    /**
     * 删除评论
     * @param id 动态id
     * @param comment: 评论内容
     */
    async deleteBlogComment(
      context,
      { blogId, comment }: { blogId: number; comment: Comment },
    ) {
      const response = await Vue.axios.post(
        '/api/v1/community/delete_comment',
        {
          id: comment.id,
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('removeBlogComment', { blogId, comment });
      }
      return rdata;
    },

    /**
     * 删除回复
     * @param id
     */
    async deleteBlogReply(context, { commentId, replyId }) {
      const response = await Vue.axios.post('/api/v1/community/delete_reply', {
        id: replyId,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('removeReply', { commentId, replyId });
      }
      return rdata;
    },

    /**
     * 点赞
     * @param id
     */
    async setBlogLike(context, blog: Blog) {
      const response = await Vue.axios.post('/api/v1/community/like', {
        id: blog.id,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setBlogLike', blog);
      }
      return rdata;
    },

    /**
     * 取消点赞
     * @param id
     */
    async setBlogDislike(context, blog: Blog) {
      const response = await Vue.axios.post('/api/v1/community/cancel_like', {
        id: blog.id,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setBlogDislike', blog);
      }
      return rdata;
    },

    /**
     * 举报
     * @param id
     */
    async setBlogReport(context, id: number) {
      const response = await Vue.axios.post('/api/v1/community/report', {
        id,
      });
      const rdata = response.data;
      return rdata;
    },

    /**
     * 获取线下活动列表
     */
    async getOfflineList({ commit }) {
      const response = await Vue.axios.get(
        '/api/v1/community/under_line_activity',
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setOfflineList', rdata.data);
      }
      return rdata;
    },

    /** 获取banner的url */
    async getBannerUrl({ commit }) {
      const response = await Vue.axios.get(
        '/api/v1/community/community_banner',
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setBannerUrl', rdata.data);
      }
      return rdata;
    },
    /**
     * 获取战队成员列表
     * @param teamId
     * @param count
     * @param lastId
     * @param forceUpdate
     */
    async getTeamMoments(
      { state, commit },
      {
        teamId,
        count,
        lastId,
        forceUpdate = false,
      }: {
        teamId: number;
        count: number;
        lastId: number | null;
        forceUpdate: boolean;
      },
    ) {
      if (isUndef(state.moments[teamId])) {
        Vue.set(state.moments, String(teamId), {
          list: [],
          loaded: false,
          requesting: false,
        });
      }
      const wrap = state.moments[teamId];
      if (wrap!.req || (wrap && wrap.loaded && !forceUpdate)) {
        return;
      }
      if (forceUpdate) {
        wrap!.loaded = false;
        wrap!.list = [];
        lastId = null;
      } else if (wrap) {
        lastId = wrap.list[wrap.list.length - 1];
      }
      commit('setMomentReq', {
        teamId,
        requesting: true,
      });
      const response = await Vue.axios.get('/api/v1/activity/team_moments', {
        params: {
          id: teamId,
          count,
          last_id: lastId,
        },
      });
      const rdata = response.data;
      commit('setMomentReq', {
        teamId,
        requesting: false,
      });
      if (rdata.status === 0) {
        const list = rdata.data;
        commit('setMoments', { teamId, moments: list });
        for (const moment of list) {
          commit(
            'user/setFollowInfo',
            {
              userId: moment.user_id,
              hasFollow: moment.has_follow,
            },
            { root: true },
          );
        }
        if (list.length < count) {
          commit('setMomentLoaded', { teamId, loaded: true });
        }
      }
      return rdata;
    },
    /**
     * 获取战队成员列表
     * @param teamId
     * @param count
     * @param lastId
     * @param forceUpdate
     */
    async getFollowMoments(
      { state, commit },
      {
        count,
        lastId,
        forceUpdate = false,
      }: { count: number; lastId: number | null; forceUpdate: boolean },
    ) {
      const wrap = state.followMoment;
      if (wrap!.req || (wrap && wrap.loaded && !forceUpdate)) {
        return;
      }
      if (forceUpdate) {
        wrap!.loaded = false;
        wrap!.list = [];
        lastId = null;
      } else if (wrap) {
        lastId = wrap.list[wrap.list.length - 1];
      }
      commit('setFollowMomentReq', { req: true });
      const response = await Vue.axios.get('/api/v1/user/follow_moment', {
        params: { count, last_id: lastId },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const list = rdata.data;
        commit('concatFollowMoments', { moments: list });
        for (const moment of list) {
          commit(
            'user/setFollowInfo',
            {
              userId: moment.user_id,
              hasFollow: true,
            },
            { root: true },
          );
        }
        if (list.length < count) {
          commit('setFollowMomentLoaded', { loaded: true });
        }
      }
      commit('setFollowMomentReq', { req: false });
      return rdata;
    },
  },
};

export default m;
